import { ParentSize } from '@visx/responsive'
import { useMemo, useRef, useState } from 'react'
import Spinner from '../Spinner'
import DateGroupSelector, { GroupBy } from '../viz/DateGroupSelector'
import { FilterBy } from '../viz/FilterBy'
import GraphHeader from '../viz/GraphHeader'
import AreaSeriesGraph, { GraphSeries } from '../viz/graphs/AreaSeriesGraph'
import { groupScheduleRateSeries } from 'app/components/analytics/scheduleRateSeries'
import { formatDate } from 'app/date.utils'
import type { QualifiedLeadsMetrics } from 'app/types/qualified-leads'

export default function ScheduleRateGraph({
  metrics,
  isLoading,
  onFilterByActivityTypeChange,
}: {
  // Only the series is read; the section passes the typed query result.
  metrics?: Pick<QualifiedLeadsMetrics, `series`>
  isLoading: boolean
  onFilterByActivityTypeChange: (activityType: string) => void
}) {
  const graphContainerRef = useRef<HTMLDivElement>(null)
  let seriesLength = metrics?.series?.length ?? 0
  let [groupedBy, setGroupedBy] = useState<GroupBy>(`weeks`)
  let groupedSeries: GraphSeries[] = useMemo(() => {
    if (seriesLength === 0) return []
    const resolvedGroup =
      groupedBy !== `days` && seriesLength <= 7
        ? `days`
        : groupedBy === `days` && seriesLength > 90
          ? `weeks`
          : groupedBy === `weeks` && seriesLength > 630
            ? `months`
            : groupedBy
    let series: GraphSeries[]
    if (isLoading || !metrics?.series) return []

    if (resolvedGroup === `days`) {
      series = metrics.series?.map((serie) => ({
        date: formatDate({ value: serie.date, dateOnly: true, format: `MMM D` }) ?? serie.date,
        scheduled: serie.scheduled,
        leads: serie.leads,
        scheduledRate: serie.scheduledRate,
        tooltip: formatDate({ value: serie.date, dateOnly: true, format: `MMM D` }) ?? serie.date,
      }))
    } else {
      series = groupScheduleRateSeries(metrics.series, resolvedGroup)
    }
    // if (filterByScheduledStatus !== `All`) {
    //   series = [...series].map(
    //     (serie) =>
    //       ({
    //         date: serie.date,
    //         tooltip: serie.tooltip,
    //         [filterByScheduledStatus]: serie[filterByScheduledStatus],
    //       } as Serie & { tooltip: string | number | Date }),
    //     [],
    //   )
    // }
    return series
  }, [groupedBy, isLoading])

  return (
    <div>
      <GraphHeader
        leftSection={[{ title: `Schedule Rate`, count: null }]}
        rightSection={[
          <FilterBy
            key={1}
            defaultFilter={`All`}
            options={[{ value: `All`, display: `All` }].concat([
              { value: `Call`, display: `Call` },
              { value: `Form`, display: `Form` },
              { value: `Chat`, display: `Chat` },
              { value: `Online Scheduler`, display: `Online Scheduler` },
              // { value: `Virtual Consultation`, display: `Virtual Consultation` },
            ])}
            onChange={(option) => onFilterByActivityTypeChange(option.value)}
          />,
          <DateGroupSelector
            key={2}
            defaultGroup={groupedBy}
            disableDays={seriesLength > 90}
            disableWeeks={seriesLength > 630 || seriesLength <= 7}
            disableMonths={seriesLength <= 31}
            onGroupChange={(group) => {
              setGroupedBy(() => {
                if ((group === `days` && seriesLength > 31) || (group === `months` && seriesLength <= 7)) {
                  return `weeks`
                } else {
                  return group
                }
              })
            }}
          />,
        ]}
      />

      <div ref={graphContainerRef} className="h-[320px] md:h-[500px]">
        {isLoading ? (
          <div className="flex h-80 items-center justify-center align-middle">
            <Spinner size={20} />
          </div>
        ) : (
          <ParentSize className="flex flex-col justify-between">
            {() => {
              return (
                groupedSeries &&
                graphContainerRef.current?.clientWidth &&
                graphContainerRef.current?.clientHeight && (
                  <AreaSeriesGraph
                    width={graphContainerRef.current?.clientWidth}
                    height={graphContainerRef.current?.clientHeight}
                    series={groupedSeries}
                  />
                )
              )
            }}
          </ParentSize>
        )}
      </div>
    </div>
  )
}
